import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Empty, Form, Input, InputNumber, Modal, Select, Space, Table, Tabs, Tag, Typography, Upload, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useWorkbenchContext } from '../../contexts/WorkbenchContext';
import { broadcastAPI, gradeManagementAPI, schoolAdminAPI } from '../../services/api';

const capabilityLabels = { manage_data: '年级数据管理', broadcast: '文字广播', send_files: '图片／文档／视频', control_devices: '设备控制', shutdown: '允许关机' };
const empty = () => ({ classes: [], teachers: [], students: [], total: 0 });
const scopeKey = (scope) => scope ? `${scope.schoolId}:${scope.grade}` : '';
const errorText = (error) => error.response?.data?.message || '操作失败，请重试';

export default function GradeManagement() {
  const workbench = useWorkbenchContext();
  const [search] = useSearchParams();
  const requestedSchool = workbench?.schoolId || search.get('schoolId');
  const [activeTab, setActiveTab] = useState(search.get('tab') === 'managers' ? 'managers' : 'classes');
  const requestedGrade = search.get('grade');
  const [scopes, setScopes] = useState([]);
  const [selected, setSelected] = useState('');
  const [contextLoading, setContextLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [snapshot, setSnapshot] = useState({ key: '', data: empty(), assignments: [], candidates: [] });
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [confirmModal, confirmHolder] = Modal.useModal();
  const version = useRef(0);
  const current = useRef('');
  current.current = selected;
  useEffect(() => { current.current = selected; return () => { current.current = ''; }; }, [selected]);
  const scope = scopes.find((item) => scopeKey(item) === selected);
  const data = snapshot.key === selected ? snapshot.data : empty();
  const assignments = snapshot.key === selected ? snapshot.assignments : [];
  const candidates = snapshot.key === selected ? snapshot.candidates : [];
  const permissions = scope?.permissions || {};

  useEffect(() => {
    let active = true;
    setContextLoading(true);
    gradeManagementAPI.context(requestedSchool ? { schoolId: requestedSchool } : {}).then((response) => {
      if (!active) return;
      if (!response.data.success) throw new Error('Context unavailable');
      const rows = response.data.data.scopes || [];
      setScopes(rows);
      setSelected(scopeKey(rows.find((item) => item.grade === requestedGrade) || rows[0]));
    }).catch((error) => { if (active) { setScopes([]); setSelected(''); message.error(errorText(error)); } })
      .finally(() => { if (active) setContextLoading(false); });
    return () => { active = false; };
  }, [requestedSchool, requestedGrade]);

  const reload = useCallback(async () => {
    if (!scope) return;
    const key = scopeKey(scope), ticket = ++version.current;
    setLoading(true); setLoadError('');
    setSnapshot({ key, data: empty(), assignments: [], candidates: [] });
    try {
      const params = { schoolId: scope.schoolId, grade: scope.grade, page };
      const [records, grants, teachers] = await Promise.all([
        scope.permissions.manage_data ? gradeManagementAPI.data(params) : Promise.resolve({ data: { data: empty() } }),
        scope.administrator ? gradeManagementAPI.assignments({ schoolId: scope.schoolId }) : Promise.resolve({ data: { data: [] } }),
        scope.administrator ? schoolAdminAPI.getTeachers({ schoolId: scope.schoolId }) : Promise.resolve({ data: { data: [] } })
      ]);
      if (ticket !== version.current || current.current !== key) return;
      setSnapshot({ key, data: records.data.data, assignments: grants.data.data, candidates: [...new Map(teachers.data.data.map((t) => [String(t.id), t])).values()] });
    } catch (error) {
      if (ticket === version.current && current.current === key) setLoadError(errorText(error));
    } finally { if (ticket === version.current && current.current === key) setLoading(false); }
  }, [scope, page]);
  useEffect(() => { reload(); return () => { version.current += 1; }; }, [reload]);

  const changeScope = (key) => { setModal(null); setFiles([]); setPage(1); setSelected(key); };
  const open = (type, record) => {
    if (!scope || loading) return;
    setFiles([]); form.resetFields();
    setModal({ type, record, scope });
    if (type === 'grant') form.setFieldsValue({ accountId: record ? String(record.account_id) : undefined,
      days: 30, ...Object.fromEntries(Object.keys(capabilityLabels).map((c) => [c, record ? Boolean(Number(record[`can_${c}`])) : ['manage_data', 'broadcast', 'send_files'].includes(c)])) });
    else if (type === 'send') form.setFieldsValue({ messageType: scope.permissions.broadcast ? 'text' : 'file', displayMode: 'popup' });
    else form.setFieldsValue({ name: record.name || record.studentName });
  };

  const submit = async () => {
    if (!modal || submitting) return;
    const action = modal, key = scopeKey(action.scope);
    try {
      const values = await form.validateFields();
      if (current.current !== key) return;
      setSubmitting(true);
      const target = { schoolId: action.scope.schoolId, grade: action.scope.grade };
      if (action.type === 'grant') {
        await gradeManagementAPI.grant({ ...target, ...values });
      } else if (action.type === 'send') {
        let mediaUrl, metadata = { grade: target.grade };
        if (values.messageType !== 'text') {
          if (!files[0]?.originFileObj) throw new Error('请选择文件');
          const upload = new FormData(); upload.append('file', files[0].originFileObj);
          const response = await broadcastAPI.uploadFile(upload);
          if (!response.data.success || !response.data.filePath) throw new Error('上传失败');
          mediaUrl = response.data.filePath;
          metadata = { ...metadata, fileName: files[0].name, originalName: files[0].name };
          if (current.current !== key) return;
        }
        const response = await broadcastAPI.sendMessage({ schoolId: target.schoolId, targetType: 'grade', targetId: target.schoolId,
          messageType: values.messageType, title: values.title, content: mediaUrl || values.content, mediaUrl,
          displayMode: values.displayMode, metadata });
        if (current.current === key) message.success(`已提交至 ${response.data.data.classCount || 0} 个班级，请查看送达结果`);
      } else {
        await gradeManagementAPI.update(action.type, action.record.id, { ...target, name: values.name });
      }
      if (current.current === key) { if (action.type !== 'send') message.success('已保存'); setModal(null); reload(); }
    } catch (error) { if (current.current === key) message.error(error.response?.data?.message || error.message || '操作失败'); }
    finally { setSubmitting(false); }
  };

  const deviceAction = (command) => {
    const captured = scope, key = scopeKey(scope);
    if (!captured) return;
    confirmModal.confirm({ title: command === 'shutdown_system' ? `确认向${captured.grade}下发关机？` : `确认控制${captured.grade}设备？`, okText: '确认下发', cancelText: '取消',
      content: command === 'shutdown_system' ? '只针对该年级班级设备。桌面端沿用120秒关机倒计时，可在本机取消。' : '只针对当前学校、当前年级班级设备。',
      onOk: async () => {
        if (current.current !== key) return;
        try {
          const response = await schoolAdminAPI.sendDeviceBatchCommand({ schoolId: captured.schoolId, grade: captured.grade, command, payload: { reason: 'grade-management' } });
          if (current.current === key) message.success(`已向 ${response.data.data.classCount || 0} 个班级提交命令`);
        } catch (error) { message.error(errorText(error)); throw error; }
      }
    });
  };

  const revoke = (record) => {
    const captured = scope, key = scopeKey(scope);
    confirmModal.confirm({ title: '撤销该教师的年级授权？', okText: '确认撤销', cancelText: '取消', content: '保留教师账号及其原有班级身份。', onOk: async () => {
      if (current.current !== key) return;
      await gradeManagementAPI.revoke(record.id, { schoolId: captured.schoolId });
      if (current.current === key) { message.success('已撤销'); reload(); }
    } });
  };

  const tabs = [];
  if (permissions.manage_data) tabs.push(
    { key: 'classes', label: '班级', children: <Table rowKey="id" loading={loading} dataSource={data.classes} columns={[
      { title: '班级', dataIndex: 'name' }, { title: '年级', dataIndex: 'grade' }, { title: '操作', render: (_, row) => <Button onClick={() => open('class', row)}>修改班级名称</Button> }
    ]} /> },
    { key: 'teachers', label: '教师', children: <Table rowKey={(row) => `${row.id}:${row.className}`} loading={loading} dataSource={data.teachers} columns={[
      { title: '教师', dataIndex: 'displayName' }, { title: '任教班级', dataIndex: 'className' }, { title: '班级角色', dataIndex: 'role' }
    ]} /> },
    { key: 'students', label: '学生', children: <Table rowKey="id" loading={loading} dataSource={data.students} pagination={{ current: page, total: data.total, pageSize: 100, showSizeChanger: false, onChange: setPage }} columns={[
      { title: '姓名', dataIndex: 'studentName' }, { title: '班级', dataIndex: 'className' }, { title: '学号', dataIndex: 'studentNumber' }, { title: '座号', dataIndex: 'seatNumber' },
      { title: '操作', render: (_, row) => <Button onClick={() => open('student', row)}>修改姓名</Button> }
    ]} /> }
  );
  if (scope?.administrator) tabs.push({ key: 'managers', label: '年级负责人', children: <>
    <Space style={{ marginBottom: 12 }}><Button type="primary" onClick={() => open('grant')}>指定当前年级负责人</Button><Typography.Text type="secondary">同一教师可以分别负责多个年级。</Typography.Text></Space>
    <Table rowKey="id" loading={loading} dataSource={assignments} columns={[
      { title: '年级', dataIndex: 'grade' }, { title: '负责人', render: (_, row) => row.displayName || row.username },
      { title: '权限', render: (_, row) => Object.entries(capabilityLabels).filter(([key]) => Number(row[`can_${key}`]) === 1).map(([key, label]) => <Tag key={key}>{label}</Tag>) },
      { title: '到期时间', dataIndex: 'expires_at', render: (v) => new Date(v).toLocaleString() },
      { title: '状态', dataIndex: 'effectiveStatus', render: (v) => ({ active: '有效', expired: '已到期', revoked: '已撤销', pending: '未开始' }[v] || v) },
      { title: '操作', render: (_, row) => <Space>{row.grade === scope.grade && <Button onClick={() => open('grant', row)}>调整／续期</Button>}{row.status !== 'revoked' && <Button danger onClick={() => revoke(row)}>撤销</Button>}</Space> }
    ]} />
  </> });

  return <>
    <Space style={{ marginBottom: 16 }} wrap><Typography.Title level={3} style={{ margin: 0 }}>年级管理</Typography.Title>
      <Select aria-label="选择学校年级" style={{ minWidth: 260 }} loading={contextLoading} value={selected || undefined} placeholder="选择学校与年级" onChange={changeScope}
        options={scopes.map((item) => ({ value: scopeKey(item), label: `${item.schoolName} · ${item.grade}` }))} />
      <Button onClick={reload} loading={loading} disabled={!scope}>刷新</Button>
    </Space>
    {!scope && !contextLoading ? <Empty description="暂无有效年级授权，请联系学校管理员" /> : scope && <>
      {!scope.administrator && <Alert showIcon type="info" style={{ marginBottom: 16 }} message={`授权范围：${scope.schoolName} · ${scope.grade}，到期：${new Date(scope.expiresAt).toLocaleString()}`} />}
      {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 16 }} />}
      <Card style={{ marginBottom: 16 }}><Space wrap>
        {(permissions.broadcast || permissions.send_files) && <Button type="primary" disabled={loading} onClick={() => open('send')}>发送广播／图片／文档／视频</Button>}
        {permissions.control_devices && <><Button onClick={() => deviceAction('lock_app')}>锁定年级客户端</Button><Button onClick={() => deviceAction('unlock_app')}>解锁年级客户端</Button></>}
        {permissions.control_devices && permissions.shutdown && <Button danger onClick={() => deviceAction('shutdown_system')}>年级设备关机</Button>}
      </Space></Card>
      <Tabs activeKey={tabs.some(item => item.key === activeTab) ? activeTab : tabs[0]?.key} onChange={setActiveTab} items={tabs} />
    </>}
    <Modal open={Boolean(modal)} title={modal?.type === 'grant' ? '设置年级负责人' : modal?.type === 'send' ? '向当前年级发送' : '修改年级数据'} cancelText="取消" okText={modal?.type === 'send' ? '发送' : '保存'} onCancel={() => { if (!submitting) setModal(null); }} onOk={submit} confirmLoading={submitting} destroyOnClose>
      <Form form={form} layout="vertical" preserve={false}>
        {modal?.type === 'grant' ? <>
          <Form.Item name="accountId" label="本校教师" rules={[{ required: true }]}><Select options={candidates.map((t) => ({ value: String(t.id), label: `${t.displayName || t.username}（${t.username}）` }))} /></Form.Item>
          <Form.Item name="days" label="从本次授权起有效天数" rules={[{ required: true }]}><InputNumber min={1} max={3650} /></Form.Item>
          {Object.entries(capabilityLabels).map(([key, label]) => <Form.Item key={key} name={key} valuePropName="checked"><Checkbox>{label}</Checkbox></Form.Item>)}
        </> : modal?.type === 'send' ? <>
          <Form.Item name="messageType" label="内容类型" rules={[{ required: true }]}><Select options={[
            ...(modal.scope.permissions.broadcast ? [{ value: 'text', label: '文字广播' }] : []),
            ...(modal.scope.permissions.send_files ? [{ value: 'image', label: '图片' }, { value: 'file', label: '文档／文件' }, { value: 'video', label: '视频' }, { value: 'audio', label: '音频' }] : [])
          ]} /></Form.Item>
          <Form.Item name="title" label="标题"><Input maxLength={255} /></Form.Item>
          <Form.Item name="displayMode" label="显示方式"><Select options={[{ value: 'popup', label: '弹窗' }, { value: 'banner', label: '横幅' }, { value: 'fullscreen', label: '全屏' }]} /></Form.Item>
          <Form.Item noStyle shouldUpdate>{({ getFieldValue }) => getFieldValue('messageType') === 'text'
            ? <Form.Item name="content" label="广播内容" rules={[{ required: true, message: '请输入内容' }]}><Input.TextArea rows={4} /></Form.Item>
            : <Form.Item label="文件"><Upload beforeUpload={(file) => file.size <= 50 * 1024 * 1024 ? false : (message.error('文件不能超过50MB'), Upload.LIST_IGNORE)} fileList={files} onChange={({ fileList }) => setFiles(fileList)} maxCount={1}><Button>选择图片／文档／视频（最大50MB）</Button></Upload></Form.Item>}
          </Form.Item>
        </> : <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input maxLength={120} /></Form.Item>}
      </Form>
    </Modal>
    {confirmHolder}
  </>;
}
